import React from "react";
import {
  FOOTER_MEDICAL_DISCLAIMER_CA,
  FOOTER_MEDICAL_DISCLAIMER_US,
} from "constants/disclaimers";
import dynamic from "next/dynamic";

import useDomain from "hooks/useDomain";

import FeedbackSurvey from "components/FeedbackSurvey";
import LegalLogo from "components/Icons/legal-logo.svg";
import FacebookIcon from "components/Icons/share/facebook.svg";
import InstagramIcon from "components/Icons/share/instagram.svg";
import TwitterIcon from "components/Icons/share/twitter.svg";
import YoutubeIcon from "components/Icons/share/youtube.svg";
import LeafbotButton from "components/LeafbotButton";
import MedicalDisclaimers from "components/MedicalDisclaimers";

import { AboutLeafly } from "./AboutLeafly";
import { AppStoreButtons } from "./AppStoreButtons";
import { BusinessSolutions } from "./BusinessSolutions";
import { Copyright } from "./Copyright";
import { DispensaryQuickLinks } from "./DispensaryQuickLinks";
import { NewsletterSignup } from "./NewsletterSignup";
import { PrivacyAndTerms } from "./PrivacyAndTerms";
import { useFooterClickEventTracker } from "./useFooterClickEventTracker";

const ChatContainer = dynamic(() => import("../chat/ChatContainer"), {
  ssr: false,
});

const HrSeperatorOne = () => (
  <hr
    className="border-light-grey lg:hidden"
    style={{ margin: "0 -10px", padding: "0 10px" }}
  />
);
const HrSeperatorTwo = () => <hr className="border-light-grey" />;

export const Footer = () => {
  const baseUrl = useDomain();
  const { createPublishFooterClickEventFunction } =
    useFooterClickEventTracker();

  return (
    <footer
      className="Accordion footer bg-leafly-white border-t border-light-grey flex flex-col text-default"
      data-allow-multiple
      data-allow-toggle
      data-test-id="footer"
    >
      <FeedbackSurvey surveyId="jAU3zvrF" />
      <div className="container flex flex-row lg:my-lg md:my-sm justify-center">
        <a
          className="a my-lg relative text-green"
          href={baseUrl}
          aria-label="Leafly"
          onClick={createPublishFooterClickEventFunction("leafly_logo_home")}
        >
          <LegalLogo width="84" height="33" />
        </a>
      </div>
      <HrSeperatorTwo />
      <div className="flex lg:flex-row lg:py-xl py-md justify-center items-center">
        <a
          className="a block px-md"
          href="http://www.facebook.com/leaflydotcom"
          title="Leafly on Facebook"
          onClick={createPublishFooterClickEventFunction("facebook")}
        >
          <FacebookIcon width={24} height={24} />
        </a>
        <a
          className="block px-md"
          href="http://twitter.com/leafly"
          title="Leafly on Twitter"
          onClick={createPublishFooterClickEventFunction("twitter")}
        >
          <TwitterIcon width={24} height={24} />
        </a>
        <a
          className="block px-md"
          href="https://www.instagram.com/heyleafly"
          title="Leafly on Instagram"
          onClick={createPublishFooterClickEventFunction("instagram")}
        >
          <InstagramIcon width={24} height={24} />
        </a>
        <a
          className="block px-md"
          href="https://www.youtube.com/channel/UCqVnkns6Gf3W7J9zWZE4EqA"
          title="Leafly on YouTube"
          onClick={createPublishFooterClickEventFunction("youtube channel")}
        >
          <YoutubeIcon width={30} height={24} />
        </a>
      </div>
      <HrSeperatorTwo />
      <div className="container lg:pb-md">
        <div className="row">
          <div className="md:col-1/2 lg:col-2/3">
            <NewsletterSignup />
          </div>
          <div className="pt-xl md:col-1/2 lg:col-1/3">
            <AppStoreButtons />
          </div>
        </div>
      </div>
      <HrSeperatorTwo />
      <div className="container">
        <div className="row lg:pb-md">
          <section
            className="col lg:col-1/4"
            data-allow-multiple
            data-allow-toggle
          >
            <BusinessSolutions />
          </section>
          <HrSeperatorOne />
          <section
            className="col lg:col-1/4"
            data-allow-multiple
            data-allow-toggle
          >
            <AboutLeafly />
          </section>
          <HrSeperatorOne />
          <section
            className="nav__stores col lg:col-1/4"
            data-allow-multiple
            data-allow-toggle
          >
            <DispensaryQuickLinks />
          </section>
          <HrSeperatorOne />
          <section
            className="nav__stores col lg:col-1/4"
            data-allow-multiple
            data-allow-toggle
          >
            <PrivacyAndTerms />
          </section>
        </div>
      </div>
      <MedicalDisclaimers
        className="container mb-xl"
        usDisclaimer={FOOTER_MEDICAL_DISCLAIMER_US}
        caDisclaimer={FOOTER_MEDICAL_DISCLAIMER_CA}
      />
      <HrSeperatorTwo />
      <section className="border-b border-light-grey container">
        <Copyright />
      </section>
      <div className="fixed bottom-0 right-0 chat">
        <ChatContainer />
      </div>
      <LeafbotButton />
    </footer>
  );
};

export default Footer;
